import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Toggles the "per piece" price next to the PDP total between the stock sale/regular price
 * (snippets/price.liquid, untouched) and a flat tier price, based on the quantity currently
 * entered on the PDP. A tier price is never combined with a sale/compare-at price - once a
 * tier applies, the stock price markup is hidden entirely rather than adjusted.
 *
 * @typedef {Object} PriceBreak
 * @property {number} quantity - Minimum quantity for this price tier
 * @property {number} price - Price in minor currency units (cents) for this tier
 *
 * @typedef {Object} BbdPricePerPieceRefs
 * @property {HTMLElement} [bbdPricePerPieceStandard] - Wrapper around the stock price block
 * @property {HTMLElement} [bbdPricePerPieceTier] - Wrapper around the tier-price text
 * @property {HTMLElement} [bbdPricePerPieceTierAmount] - The text element displaying the tier price
 *
 * @extends {Component<BbdPricePerPieceRefs>}
 */
class BbdPricePerPieceComponent extends Component {
  /** @type {PriceBreak[]} */
  #priceBreaks = [];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#parsePriceBreaks();
    this.#attachEventListeners();
    this.#updateDisplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Parses tier price breaks (in minor currency units) from the data attribute. Unlike
   * bbd-total-price.js, no base-price fallback entry is added - an empty result here
   * correctly means "no tier applies", which is what decides whether to show the stock
   * sale/regular price instead.
   */
  #parsePriceBreaks() {
    this.#priceBreaks = [];

    const priceBreaksData = this.dataset.priceBreaks;
    if (priceBreaksData) {
      const breaks = JSON.parse(priceBreaksData);
      for (const { quantity, price } of breaks) {
        if (quantity && !isNaN(parseInt(price))) {
          this.#priceBreaks.push({ quantity: parseInt(quantity), price: parseInt(price) });
        }
      }
    }

    // Sort by quantity descending for efficient lookup
    this.#priceBreaks.sort((a, b) => b.quantity - a.quantity);
  }

  #attachEventListeners() {
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
  }

  /**
   * Handles quantity selector updates
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#updateDisplay();
  };

  /**
   * Handles product select (variant change) events. This component is never a morph
   * target from product-form.js, so it must refresh its own price data independently,
   * mirroring the pattern used by bbd-total-price.js.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newPricePerPiece = html.querySelector(`bbd-price-per-piece[data-block-id="${this.dataset.blockId}"]`);
        if (!newPricePerPiece) return;

        this.dataset.variantPrice = newPricePerPiece.dataset.variantPrice;
        this.dataset.priceBreaks = newPricePerPiece.dataset.priceBreaks;
        this.dataset.moneyFormat = newPricePerPiece.dataset.moneyFormat;
        this.dataset.currency = newPricePerPiece.dataset.currency;

        // Guards against the same hidden-option/variant-matching mismatch that can make
        // Liquid silently omit a price label (see variant-main-picker.liquid) - a fetched
        // fragment with no real price content (empty, or just the '&nbsp;' fallback from
        // snippets/price.liquid) must never overwrite an already-correct display.
        const newStandard = newPricePerPiece.querySelector('[data-key="bbd-price-per-piece-standard"]');
        const currentStandard = this.querySelector('[data-key="bbd-price-per-piece-standard"]');
        if (newStandard && currentStandard && newStandard.textContent.trim() !== '') {
          currentStandard.replaceChildren(...newStandard.cloneNode(true).childNodes);
        }

        this.#parsePriceBreaks();
        this.#updateDisplay();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bbd-price-per-piece] Product select promise rejected:', error);
      });
  };

  /**
   * Gets the current quantity entered on the PDP, independent of what's already in the cart
   * @returns {number}
   */
  #getCurrentQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    if (!quantityInput) return 1;

    return parseInt(quantityInput.value) || 1;
  }

  /**
   * Toggles between the stock sale/regular price and the flat tier price based on quantity
   */
  #updateDisplay() {
    const standard = this.querySelector('[data-key="bbd-price-per-piece-standard"]');
    const tier = this.querySelector('[data-key="bbd-price-per-piece-tier"]');
    const tierAmount = this.querySelector('[data-key="bbd-price-per-piece-tier-amount"]');
    if (!standard || !tier || !tierAmount) return;

    const quantity = this.#getCurrentQuantity();
    const priceBreak = this.#priceBreaks.find((pb) => quantity >= pb.quantity);

    if (priceBreak) {
      const { moneyFormat, currency } = this.dataset;
      tierAmount.textContent = formatMoney(priceBreak.price, moneyFormat || '{{amount}}', currency || '');
      standard.setAttribute('hidden', '');
      tier.removeAttribute('hidden');
    } else {
      tier.setAttribute('hidden', '');
      standard.removeAttribute('hidden');
    }
  }
}

if (!customElements.get('bbd-price-per-piece')) {
  customElements.define('bbd-price-per-piece', BbdPricePerPieceComponent);
}
